import { useEffect, useRef } from "react";
import { ArrowUpRight, ChevronDown, MessageSquare } from "lucide-react";
import type { RescueCase } from "@io-spoke/contracts/rescue";

function evidenceDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(date);
}

function safeSourceUrl(value?: string) {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:" ? value : null;
  } catch { return null; }
}

function EvidenceCard({ item }: { item: RescueCase["evidence"][number] }) {
  const details = useRef<HTMLDetailsElement>(null);
  const sourceUrl = safeSourceUrl(item.sourceUrl);
  useEffect(() => {
    const revealAnchor = () => {
      let hash: string;
      try { hash = decodeURIComponent(window.location.hash.slice(1)); } catch { return; }
      if (hash === item.id && details.current) details.current.open = true;
    };
    revealAnchor();
    window.addEventListener("hashchange", revealAnchor);
    return () => window.removeEventListener("hashchange", revealAnchor);
  }, [item.id]);
  return <details id={item.id} ref={details} className="group/evidence scroll-mt-4 rounded-lg border bg-card open:border-orange-500/60 open:bg-orange-500/5">
    <summary className="flex min-h-11 cursor-pointer list-none items-start gap-3 rounded-lg p-4 outline-none transition-colors hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none">
      <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-orange-700 dark:text-orange-300" aria-hidden="true" />
      <span className="min-w-0 flex-1"><span className="block break-words text-sm font-medium">{item.title}</span><span className="mt-1 block text-xs text-muted-foreground">{item.channel} · {evidenceDate(item.occurredAt)}</span><span className="mt-2 block break-words text-xs text-muted-foreground">{item.sourceLabel}</span></span>
      <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 group-open/evidence:rotate-180" aria-hidden="true" />
    </summary>
    <div className="space-y-3 border-t px-4 pb-4 pt-3">
      <p className="text-xs font-medium text-muted-foreground">Source excerpt</p>
      <blockquote className="whitespace-pre-wrap break-words border-l-2 border-orange-500/50 pl-3 text-sm leading-6">{item.excerpt}</blockquote>
      {item.sourceId ? <p className="break-all text-xs text-muted-foreground">Source reference: {item.sourceId}</p> : null}
      {sourceUrl ? <a href={sourceUrl} target="_blank" rel="noreferrer" className="inline-flex min-h-11 items-center gap-1 rounded text-sm font-medium text-orange-800 underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:text-orange-300">Open source <ArrowUpRight className="h-4 w-4" aria-hidden="true" /></a> : null}
    </div>
  </details>;
}

export function RescueEvidenceCards({ evidence }: { evidence: RescueCase["evidence"] }) {
  return <section aria-labelledby="rescue-evidence-title" className="border-b p-5 sm:p-6 xl:border-b-0 xl:border-r">
    <h3 id="rescue-evidence-title" className="text-sm font-semibold">Explore the evidence</h3>
    <p className="mt-1 text-xs leading-5 text-muted-foreground">Open a card to inspect the source excerpt behind this assessment.</p>
    {evidence.length ? <div className="mt-4 space-y-3">{evidence.map((item) => <EvidenceCard key={item.id} item={item} />)}</div> : <p className="mt-4 rounded-md border border-dashed p-4 text-sm text-muted-foreground">No source evidence is attached. Add evidence before relying on this assessment.</p>}
  </section>;
}
